import { MessageSquare, Plus, X } from 'lucide-react';

const Sidebar = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  isOpen,
  onClose,
}) => {
  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-nova-bg border-r border-nova-green/20 z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header with close button */}
          <div className="flex items-center justify-between p-4 border-b border-nova-green/20">
            <h2 className="text-lg font-semibold text-white">Chat History</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-nova-green/20 rounded-lg transition-colors"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* New Chat Button */}
          <div className="p-4 border-b border-nova-green/20">
            <button
              onClick={onNewChat}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-nova-green hover:bg-nova-green/80 text-white font-medium rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>New Chat</span>
            </button>
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {sessions && sessions.length > 0 ? (
              sessions.map((session) => (
                <button
                  key={session.sessionId}
                  onClick={() => onSelectSession(session.sessionId)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    session.sessionId === activeSessionId
                      ? 'bg-nova-green/20 border border-nova-green/40'
                      : 'bg-nova-card hover:bg-nova-card-hover border border-nova-green/10'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    <MessageSquare
                      className={`w-4 h-4 mt-1 flex-shrink-0 ${
                        session.sessionId === activeSessionId
                          ? 'text-nova-green'
                          : 'text-gray-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          session.sessionId === activeSessionId
                            ? 'text-white'
                            : 'text-gray-300'
                        }`}
                      >
                        {session.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(session.lastActive)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No chat history yet</p>
                <p className="text-xs mt-1">Start a new chat to begin</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // Format as date for older items
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default Sidebar;
